import { useQueries, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { serviceApi } from '~/features';
import { clearParams } from '~/shared/utils/clearParams';
import type {
  CampaignDetailsResponse,
  CampaignMetricsFilters,
  CampaignMetricsRequest,
  CampaignMetricsResponse,
  CampaignVolumetriaResponse,
} from '~/types/monitoring';

import { GET_CAMPAIGN_DETAILS_QUERY_KEY } from './useGetCampaignDetails';

export const GET_CAMPAIGN_METRICS_QUERY_KEY = 'GET_CAMPAIGN_METRICS_QUERY';

function transformFiltersToRequest(
  filters: CampaignMetricsFilters
): CampaignMetricsRequest {
  const request: CampaignMetricsRequest = {};

  if (filters.campaignId) {
    request.IdCampanha = Number(filters.campaignId);
  }

  if (filters.segmentationType !== undefined) {
    request.TipoSegmentacao = filters.segmentationType;
  }

  if (filters.metric) {
    request.Metrica = filters.metric;
  }

  if (filters.startDate) {
    const date = filters.startDate;
    request.DataInicio = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  }

  if (filters.endDate) {
    const date = filters.endDate;
    request.DataFim = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  }

  if (filters.status !== undefined) {
    request.Status = filters.status;
  }

  if (filters.campaignType !== undefined) {
    request.TipoCampanha = filters.campaignType;
  }

  if (filters.advertiserId) {
    request.AnuncanteId = filters.advertiserId;
  }

  return request;
}

async function getCampaignVolumetria(
  params: CampaignMetricsRequest,
  page: number,
  count: number
) {
  const clearedParams = clearParams({
    ...params,
    Page: page,
    Count: count,
  } as Record<string, unknown>);

  const response = await serviceApi.get<CampaignVolumetriaResponse>(
    '/CalculoCampanha/volumetria-registros-campanhas',
    {
      params: clearedParams,
    }
  );

  return response.data;
}

async function getCampaignDetails(
  campaignId: number,
  isSponsoredProducts: boolean
) {
  const endpoint = isSponsoredProducts
    ? `/CampanhaProdutosPatrocinados?id=${campaignId}`
    : `/Campanha?id=${campaignId}`;

  const response = await serviceApi.get<CampaignDetailsResponse>(endpoint);
  return response.data;
}

function formatPeriodo(startDate?: string, endDate?: string): string {
  try {
    if (!startDate && !endDate) {
      return '-';
    }

    const formatSingleDate = (dateStr: string): string => {
      const [datePart] = dateStr.split(' ');
      const [day, month, year] = datePart.split('/');
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return format(date, 'dd/MM/yyyy');
    };

    if (startDate && !endDate) {
      return `A partir de ${formatSingleDate(startDate)}`;
    }

    if (!startDate && endDate) {
      return `Até ${formatSingleDate(endDate)}`;
    }

    if (startDate && endDate) {
      const formattedStart = formatSingleDate(startDate);
      const formattedEnd = formatSingleDate(endDate);
      return `${formattedStart} - ${formattedEnd}`;
    }

    return '-';
  } catch (error) {
    if (startDate && endDate) {
      return `${startDate} - ${endDate}`;
    }
    if (startDate) {
      return `A partir de ${startDate}`;
    }
    if (endDate) {
      return `Até ${endDate}`;
    }
    return '-';
  }
}

function formatSegmentacao(
  segments?: CampaignDetailsResponse['segments']
): string {
  if (!segments) {
    return '-';
  }

  const segmentParts: string[] = [];

  if (segments.departamentos && segments.departamentos.length > 0) {
    segmentParts.push('Departamentos');
  }

  if (segments.tipoNegocio && segments.tipoNegocio.length > 0) {
    segmentParts.push('Tipo de Negócio');
  }

  if (segments.estado && segments.estado.length > 0) {
    segmentParts.push('Estados');
  }

  return segmentParts.length > 0 ? segmentParts.join(', ') : '-';
}

function translateStatus(status: string): string {
  const statusMap: Record<string, string> = {
    INACTIVE: 'Inativa',
    ACTIVE: 'Ativa',
    DRAFT: 'Rascunho',
    REVIEW: 'Em Revisão',
    ATTENTION: 'Atenção',
    PAUSED: 'Pausada',
    DONE: 'Concluída',
  };

  return statusMap[status] || status;
}

export function useGetCampaignMetrics(
  filters: CampaignMetricsFilters,
  enabled = true
) {
  const params = transformFiltersToRequest(filters);
  const isSponsoredProducts = filters.campaignType === 2;

  const volumetriaQuery = useQuery({
    queryKey: [
      GET_CAMPAIGN_METRICS_QUERY_KEY,
      'volumetria',
      params,
      filters.page,
      filters.count,
    ],
    queryFn: () => getCampaignVolumetria(params, filters.page, filters.count),
    staleTime: 1000 * 60,
    enabled,
  });

  const volumetriaList = volumetriaQuery.data?.list || [];
  const campaignIds = volumetriaList.map(item => item.idCampaign);

  const campaignDetailsQueries = useQueries({
    queries: campaignIds.map(id => ({
      queryKey: [GET_CAMPAIGN_DETAILS_QUERY_KEY, id, isSponsoredProducts],
      queryFn: () => getCampaignDetails(id, isSponsoredProducts),
      staleTime: 1000 * 60 * 5,
      enabled: volumetriaQuery.isSuccess && id > 0,
    })),
  });

  const combinedData: CampaignMetricsResponse[] | undefined =
    volumetriaList.map((volumetria, index) => {
      const detailsQuery = campaignDetailsQueries[index];
      const details = detailsQuery?.data;

      return {
        campaignId: volumetria.idCampaign,
        campaignName: details?.name || 'Carregando...',
        advertiserName: details?.advertiser?.name || 'Carregando...',
        status: details?.status ? translateStatus(details.status) : '',
        tipoCampanha: isSponsoredProducts ? 'Produtos patrocinados' : 'Banners',
        periodo: details
          ? formatPeriodo(details.startDate, details.endDate)
          : 'Carregando...',
        impressions: volumetria.impressions,
        clicks: volumetria.clicks,
        ctr: volumetria.valueMetrics,
        segmentacao: details
          ? formatSegmentacao(details.segments)
          : 'Carregando...',
      };
    });

  const isLoading =
    volumetriaQuery.isLoading || campaignDetailsQueries.some(q => q.isLoading);
  const isError =
    volumetriaQuery.isError || campaignDetailsQueries.some(q => q.isError);
  const isSuccess =
    volumetriaQuery.isSuccess &&
    campaignDetailsQueries.every(q => q.isSuccess || !q.isLoading);

  const apiTotal = volumetriaQuery.data?.total || 0;
  const totalPages = Math.ceil(apiTotal / filters.count);

  return {
    data: {
      list: combinedData || [],
      count: filters.count,
      page: filters.page,
      total: apiTotal,
      totalPages: totalPages > 0 ? totalPages : 1,
    },
    isLoading,
    isError,
    isSuccess,
    refetch: volumetriaQuery.refetch,
  };
}

export type UseGetCampaignMetricsReturn = ReturnType<
  typeof useGetCampaignMetrics
>['data'];
